import { useCallback, useEffect, useRef } from 'react';

type CallbackFunction<TArgs extends unknown[], TResult> = (...args: TArgs) => TResult;

// Return a callback with stable identity that still executes the latest callback body.
// Useful when passing handlers to memoized/deep-compared consumers where changing
// function references would trigger unnecessary recomputation or re-initialization.
export function useStableCallback<TArgs extends unknown[], TResult>(
    callback: CallbackFunction<TArgs, TResult>
): CallbackFunction<TArgs, TResult> {
    const callbackRef = useRef(callback);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    return useCallback((...args: TArgs) => callbackRef.current(...args), []);
}
